import { makeObservable, observable, action } from "mobx";
import { Auth } from '../api';
import { getAccessToken, getRefreshToken, decodeJWT, createRefreshRequest } from '../api/config';

export default class AuthStore {
  isLoggedIn = false;
  user = {};
  isRestoringAuth = true;
  tokenExpirationTimer = null;
  tokenRefreshTimer = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      isLoggedIn: observable,
      user: observable,
      isRestoringAuth: observable,
      PasswordLogin: action,
      logout: action,
      setTokens: action,
      setUser: action,
      restoreAuth: action,
      setRestoringAuth: action,
      clearTokenTimers: action,
      setTokenTimers: action,
      refreshToken: action
    });

    this.restoreAuth();
  }

  setRestoringAuth(restoring) {
    this.isRestoringAuth = restoring;
  }

  clearTokenTimers() {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  setTokenTimers(expirationTime) {
    this.clearTokenTimers();

    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    // Set timer for token refresh 5 minutes before expiration
    const refreshTime = timeUntilExpiration - 300000; // 5 minutes
    if (refreshTime > 0) {
      this.tokenRefreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    } else if (timeUntilExpiration > 0) {
      this.refreshToken();
    } else {
      this.logout();
      this.rootStore.uiStore?.showNotification('Your session has expired. Please login again.', 'info');
    }
  }

  async refreshToken() {
    const token = getRefreshToken();
    if (!token) {
      return false;
    }

    try {
      const request = createRefreshRequest();
      const response = await request.post('/auth/refreshToken', { refreshToken: token });

      if (response.data && response.data.success) {
        const { accessToken, refreshToken } = response.data.response;
        this.setTokens(accessToken, refreshToken);
        this.setUser(this.user);
        return true;
      } else {
        this.logout();
        this.rootStore.uiStore?.showNotification('Your session has expired. Please login again.', 'info');
        return false;
      }
    } catch (error) {
      if (error.response?.status === 401) {
        this.logout();
        this.rootStore.uiStore?.showNotification('Your session has expired. Please login again.', 'info');
      } else {
        setTimeout(() => {
          this.refreshToken();
        }, 30000);
      }
      return false;
    }
  }

  isTokenExpired() {
    const token = getAccessToken();
    if (!token || token === "") {
      return true;
    }
    try {
      const decoded = decodeJWT(token);
      const exp = decoded.exp * 1000;
      return Date.now() >= exp;
    } catch (e) {
      return true;
    }
  }

  restoreAuth() {
    const token = getAccessToken();
    const userData = window.localStorage.getItem('user');
    
    if (token && token !== "") {
      const isExpired = this.isTokenExpired();
      if (!isExpired) {
        this.isLoggedIn = true;
        if (userData) {
          try {
            this.user = JSON.parse(userData);
          } catch (_) {
            this.user = {};
          }
        }
        try {
          const decoded = decodeJWT(token);
          const expirationTime = decoded.exp * 1000;
          this.setTokenTimers(expirationTime);
        } catch (_) {
          this.logout();
        }
      } else {
        this.logout();
      }
    } else {
      this.isLoggedIn = false;
      this.user = {};
    }
    this.setRestoringAuth(false);
  }

  async PasswordLogin(email, Password) {
    try {
      const retObj = await Auth.PasswordLogin(email, { Password });
      if (retObj.data && retObj.data.response) {
        const { accessToken, refreshToken, user } = retObj.data.response;
        this.setTokens(accessToken, refreshToken);
        this.isLoggedIn = true;
        this.setUser(user);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  setTokens(accessToken, refreshToken) {
    if (!accessToken || accessToken === "") return;

    window.localStorage.setItem('accessToken', accessToken);
    window.localStorage.setItem('refreshToken', refreshToken);

    try {
      const decoded = decodeJWT(accessToken);
      const expirationTime = decoded.exp * 1000;
      this.setTokenTimers(expirationTime);
    } catch (_) {
    }
  }

  setUser(userData) {
    this.user = userData;
    window.localStorage.setItem('user', JSON.stringify(userData));
  }

  logout() {
    this.clearTokenTimers();
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem('user');
    this.isLoggedIn = false;
    this.user = {};
  }
}
